import React, { useReducer } from 'react'
import FileSaver from 'file-saver'
import { useNotify } from 'react-admin'
import { useTranslation } from 'react-i18next'

import Loadable, {
  TLoadable,
  TLoadableAny,
  TLoadableSuccess,
} from 'src/utils/Loadable'
import type { IAlarm } from '@obvious.tech/constellation'
import type { TEntity } from 'src/utils/api'
import type { TAlarmReportProps } from 'src/parser/shared/PDF/Provider/index'
import type { TAdditionalDetails } from 'src/parser/entities/Alarm/Preview/Provider/useAdditionalDetails'

import {
  collectAlarmArchiveEntries,
  zipEntries,
} from 'src/parser/entities/Alarm/Export/alarmArchive'
import {
  INITIAL_EXPORT_STATE,
  exportReducer,
  TExportState,
} from 'src/parser/entities/Alarm/Export/exportState'
import {
  toExportError,
  TExportError,
} from 'src/parser/entities/Alarm/Export/exportErrors'
import {
  RUNNING_NOTICE_DELAY,
  notifyFailure,
  notifyOutcome,
  notifyRunning,
} from 'src/parser/entities/Alarm/Export/exportNotifications'

type TPdfSource = {
  loading: boolean
  blob: Blob | null
  error: string | null
}

export type TUseDownloadZipProps = Partial<
  Omit<TAlarmReportProps, 'entity'>
> & {
  entity?: Pick<TEntity<IAlarm>, 'id'>
  pdf: TPdfSource
}

export type TUseDownloadZipResult = [() => Promise<void>, TExportState]

const useDownloadZip = (props: TUseDownloadZipProps): TUseDownloadZipResult => {
  const [state, dispatch] = useReducer(exportReducer, INITIAL_EXPORT_STATE)

  const runningRef = React.useRef(false)
  const notify = useNotify()
  const { t } = useTranslation()
  const { attachments, additionalDetails, pdf } = props
  const alarmId = props.entity?.id

  const downloadZip = React.useCallback(async () => {
    if (runningRef.current || state.status === 'running') return
    if (pdf.loading || pdf.error !== null || pdf.blob === null) return
    if (alarmId === undefined) return

    const pdfBlob = pdf.blob

    let runningNotice: ReturnType<typeof setTimeout> | undefined = setTimeout(
      () => {
        runningNotice = undefined
        notifyRunning(notify, t)
      },
      RUNNING_NOTICE_DELAY,
    )

    const settle = (error: TExportError): void => {
      dispatch({ type: 'FAILURE', error })
      notifyFailure(notify, t, error)
    }

    runningRef.current = true
    dispatch({ type: 'START', total: 1 })

    try {
      const attachmentsData = isSuccess(attachments) ? attachments.data : []
      const additionalDetailsData: TAdditionalDetails = isSuccess(
        additionalDetails,
      )
        ? additionalDetails.data
        : {}
      const attachmentsError = checkError(attachments)
      const additionalDetailsError = checkError(additionalDetails)

      const {
        entries,
        failures: assetFailures,
        addEntry,
      } = await collectAlarmArchiveEntries({
        alarmId,
        prefix: '',
        pdfBlob,
        attachments: attachmentsData,
        additionalDetails: additionalDetailsData,
      })

      const failures = [...assetFailures]

      if (additionalDetailsError !== null) {
        addEntry('ERROR_additional_details.txt', additionalDetailsError)
        failures.push({
          code: 'REPORT_DATA_INCOMPLETE',
          alarmId,
          cause: additionalDetailsError,
        })
      }
      if (attachmentsError !== null) {
        addEntry('ERROR_attachments.txt', attachmentsError)
        failures.push({
          code: 'REPORT_DATA_INCOMPLETE',
          alarmId,
          cause: attachmentsError,
        })
      }
      assetFailures.forEach(({ name, cause }) => {
        addEntry(`ERROR-${name}.txt`, cause)
      })

      let zipBlob: Blob
      try {
        zipBlob = await zipEntries(entries)
      } catch (cause) {
        settle(toExportError(cause, 'ARCHIVE_FAILED'))
        return
      }

      const fileName = `report-${alarmId}.zip`

      try {
        FileSaver.saveAs(zipBlob, fileName)
      } catch (cause) {
        settle(toExportError(cause, 'SAVE_FAILED'))
        return
      }

      dispatch({ type: 'SUCCESS', fileName, failures })
      notifyOutcome(notify, t, failures)
    } catch (cause) {
      settle(toExportError(cause, 'UNKNOWN'))
    } finally {
      if (runningNotice !== undefined) clearTimeout(runningNotice)
      runningRef.current = false
    }
  }, [
    attachments,
    additionalDetails,
    state.status,
    alarmId,
    pdf.blob,
    pdf.error,
    pdf.loading,
    notify,
    t,
  ])

  return [downloadZip, state]
}

export default useDownloadZip

const isSuccess = <T>(
  resource: TLoadable<T, NonNullable<unknown>> | undefined,
): resource is TLoadableSuccess<T> =>
  resource != null && Loadable.isSuccess(resource)

const checkError = (resource: TLoadableAny | undefined): string | null => {
  if (resource == null) return null
  if (!Loadable.isFailure(resource)) return null

  const error: unknown = resource.error
  return String(error)
}
